/**
 * Was bei einem Kunden gerade ansteht — für die Zeile in der Kundenliste und den
 * Kasten „Jetzt wichtig" in der Kundenansicht (Entwurf B, 03.11.2026).
 *
 * Die Lage rechnet der Server (`GET /kontor/kunden/lage`, kunden_lage_store.py);
 * hier wird nur ausgewählt, welcher Satz in die eine Zeile passt. Reihenfolge:
 * offene Aufgabe vor wartendem Angebot vor offener Rechnung vor laufendem Projekt
 * vor Pflege — was Benjamin tun muss, steht vor dem, was einfach läuft.
 */
import type { Kunde } from "./types";
import { MONATE } from "./kundeFormular";
import { statusWort } from "./projekte";

export type KundeLage = {
  kunde_id: string;
  aufgaben: number;
  wichtig: boolean;
  aufgabe: string;
  zuletzt: string;
  projekte: number;
  projekt_id: string;
  projekt_titel: string;
  projekt_status: string;
  angebot_seit: string;
  rechnungen_offen: number;
};

/** aufgabe = etwas zu tun · warten = Antwort oder Geld fehlt · projekt = läuft · pflege · "" = nur der Ort */
export type HinweisTon = "aufgabe" | "warten" | "projekt" | "pflege" | "";
export type Hinweis = { text: string; ton: HinweisTon };

/** Ab so vielen Tagen ohne Antwort fragt die Liste nach: „Angebot wartet seit …". */
export const ANGEBOT_NACHFASSEN_TAGE = 7;

export function tageSeit(iso: string, heute = new Date()): number {
  if (!iso) return 0;
  const d = new Date(iso.length === 10 ? `${iso}T00:00:00` : iso);
  if (Number.isNaN(d.getTime())) return 0;
  const a = Date.UTC(heute.getFullYear(), heute.getMonth(), heute.getDate());
  const b = Date.UTC(d.getFullYear(), d.getMonth(), d.getDate());
  return Math.max(0, Math.round((a - b) / 86_400_000));
}

/** Zwei Buchstaben für den Kreis: Vor- und Nachname, bei Firmen die ersten zwei Wörter. */
export function kuerzel(name: string): string {
  const woerter = (name || "").replace(/[^\p{L}\p{N}\s-]/gu, " ").split(/\s+/).filter(Boolean)
    .filter((w) => !/^(gbr|gmbh|eg|kg|ag|ug|mbh|co|e\.?v\.?|und|&)$/i.test(w));
  if (!woerter.length) return "?";
  const erstes = woerter[0][0];
  const zweites = woerter.length > 1 ? woerter[woerter.length - 1][0] : woerter[0][1] || "";
  return (erstes + zweites).toUpperCase();
}

const TOENE = ["akzent", "blau", "gruen", "gelb", "lila"] as const;
export type KreisTon = (typeof TOENE)[number];

/** Gleicher Kunde, gleiche Farbe — aus der Nummer, nicht zufällig. */
export function kreisTon(id: string): KreisTon {
  let h = 0;
  for (const z of id || "") h = (h * 31 + z.charCodeAt(0)) >>> 0;
  return TOENE[h % TOENE.length];
}

/** Erster Buchstabe für die Sprungleiste; Umlaute zum Grundbuchstaben, alles andere „#". */
export function buchstabe(name: string): string {
  const z = (name || "").trim().charAt(0).toLocaleUpperCase("de");
  const ohne = z.normalize("NFD").replace(/[̀-ͯ]/g, "");
  return /^[A-Z]$/.test(ohne) ? ohne : "#";
}

/** „Apr – Okt" bei zusammenhängenden Monaten, sonst „Apr, Jun, Sep". */
export function pflegeText(pm: Record<string, string> | undefined | null): string {
  const n = Object.keys(pm || {}).map((k) => parseInt(k, 10)).filter((m) => m >= 1 && m <= 12).sort((a, b) => a - b);
  if (!n.length) return "";
  const kurz = (m: number) => MONATE[m - 1][2];
  const am_stueck = n.every((m, i) => i === 0 || m === n[i - 1] + 1);
  return am_stueck && n.length > 2 ? `${kurz(n[0])} – ${kurz(n[n.length - 1])}` : n.map(kurz).join(", ");
}

export function brauchtDich(l: KundeLage | undefined): boolean {
  if (!l) return false;
  return l.aufgaben > 0 || l.rechnungen_offen > 0
    || (!!l.angebot_seit && tageSeit(l.angebot_seit) >= ANGEBOT_NACHFASSEN_TAGE);
}

/** Der eine Satz unter dem Namen. `ort` ist der Rückfall, wenn nichts ansteht. */
export function hinweis(k: Kunde, l: KundeLage | undefined, ort: string): Hinweis {
  if (l && l.aufgaben > 0) {
    return { text: l.aufgabe + (l.aufgaben > 1 ? ` · +${l.aufgaben - 1}` : ""), ton: "aufgabe" };
  }
  if (l?.angebot_seit) {
    const t = tageSeit(l.angebot_seit);
    if (t >= 3) return { text: `Angebot wartet seit ${t} Tagen`, ton: "warten" };
  }
  if (l && l.rechnungen_offen > 0) {
    return { text: l.rechnungen_offen === 1 ? "Rechnung offen" : `${l.rechnungen_offen} Rechnungen offen`, ton: "warten" };
  }
  if (l && l.projekte > 0) {
    return { text: [l.projekt_titel, statusWort(l.projekt_status)].filter(Boolean).join(" · "), ton: "projekt" };
  }
  const pflege = pflegeText(k.pflege_monate);
  if (pflege) return { text: `Pflege · ${pflege}`, ton: "pflege" };
  return { text: ort, ton: "" };
}

/** „heute", „gestern", „vor 4 Tagen", sonst das Datum. */
export function wannText(iso: string): string {
  if (!iso) return "";
  const t = tageSeit(iso);
  if (t === 0) return "heute";
  if (t === 1) return "gestern";
  if (t < 14) return `vor ${t} Tagen`;
  const d = new Date(iso);
  return d.toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "2-digit" });
}
